export interface ElOpts {
  class?: string;
  text?: string;
  html?: string;
  attrs?: Record<string, string>;
  on?: Record<string, EventListener>;
  style?: Partial<CSSStyleDeclaration>;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: ElOpts = {},
  children: Array<Node | string> = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (opts.class) node.className = opts.class;
  if (opts.text !== undefined) node.textContent = opts.text;
  if (opts.html !== undefined) node.innerHTML = opts.html;
  if (opts.attrs) for (const [k, v] of Object.entries(opts.attrs)) node.setAttribute(k, v);
  if (opts.on) for (const [evt, fn] of Object.entries(opts.on)) node.addEventListener(evt, fn);
  if (opts.style) Object.assign(node.style, opts.style);
  for (const child of children) {
    if (typeof child === "string") node.appendChild(document.createTextNode(child));
    else node.appendChild(child);
  }
  return node;
}

export function clear(node: HTMLElement): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function svgIcon(path: string, viewBox = "0 0 24 24"): SVGElement {
  const NS = "http://www.w3.org/2000/svg";
  const root = document.createElementNS(NS, "svg");
  root.setAttribute("viewBox", viewBox);
  root.setAttribute("fill", "none");
  root.setAttribute("stroke", "currentColor");
  root.setAttribute("stroke-width", "1.8");
  root.setAttribute("stroke-linecap", "round");
  root.setAttribute("stroke-linejoin", "round");
  const p = document.createElementNS(NS, "path");
  p.setAttribute("d", path);
  root.appendChild(p);
  return root;
}

export const ICONS = {
  gear: "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm8.94 4a8.94 8.94 0 0 0-.11-1.39l2.03-1.59-2-3.46-2.39.96a8.95 8.95 0 0 0-2.4-1.39l-.36-2.54h-4l-.36 2.54a8.95 8.95 0 0 0-2.4 1.39l-2.39-.96-2 3.46 2.03 1.59A8.94 8.94 0 0 0 4.06 12c0 .47.04.93.11 1.39l-2.03 1.59 2 3.46 2.39-.96a8.95 8.95 0 0 0 2.4 1.39l.36 2.54h4l.36-2.54a8.95 8.95 0 0 0 2.4-1.39l2.39.96 2-3.46-2.03-1.59c.07-.46.11-.92.11-1.39Z",
  chart: "M4 19V5m6 14V9m6 10v-6m6 6V11",
  speaker: "M11 5 6 9H2v6h4l5 4V5Zm5.5 4a4 4 0 0 1 0 6m3-9a8 8 0 0 1 0 12",
  close: "M6 6l12 12M6 18 18 6",
  download: "M12 3v12m0 0-4-4m4 4 4-4M5 21h14",
  upload: "M12 21V9m0 0-4 4m4-4 4 4M5 3h14",
  reset: "M3 12a9 9 0 1 0 3-6.7L3 8m0-5v5h5",
};

export function crestSVG(): SVGElement {
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  const rows = 4, cols = 4;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if ((r + c) % 2 === 0) {
        const rect = document.createElementNS(NS, "rect");
        rect.setAttribute("x", String(c * 6));
        rect.setAttribute("y", String(r * 6));
        rect.setAttribute("width", "6");
        rect.setAttribute("height", "6");
        rect.setAttribute("fill", "#BC4639");
        svg.appendChild(rect);
      }
    }
  }
  return svg;
}

let toastTimer: number | null = null;
export function toast(msg: string): void {
  let node = document.getElementById("toast") as HTMLDivElement | null;
  if (!node) {
    node = el("div", { attrs: { id: "toast" }, class: "toast" });
    document.body.appendChild(node);
  }
  node.textContent = msg;
  node.classList.add("show");
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    node!.classList.remove("show");
  }, 1600);
}

export interface SheetCtl {
  open: () => void;
  close: () => void;
  body: HTMLElement;
  setTitle: (t: string) => void;
}

export function createSheet(id: string, title: string): SheetCtl {
  const existing = document.getElementById(`sheet-${id}`);
  if (existing) existing.remove();
  const backdropExisting = document.getElementById(`backdrop-${id}`);
  if (backdropExisting) backdropExisting.remove();

  const backdrop = el("div", { attrs: { id: `backdrop-${id}` }, class: "sheet-backdrop" });
  const handle = el("div", { class: "sheet-handle" });
  const headTitle = el("h2", { text: title });
  const closeBtn = el(
    "button",
    { class: "icon-btn", attrs: { "aria-label": "Schließen" } },
    [svgIcon(ICONS.close)]
  );
  const head = el("div", { class: "sheet-head" }, [headTitle, closeBtn]);
  const body = el("div", { class: "sheet-body" });
  const sheet = el("div", { attrs: { id: `sheet-${id}` }, class: "sheet" }, [handle, head, body]);

  document.body.appendChild(backdrop);
  document.body.appendChild(sheet);

  const close = () => {
    backdrop.classList.remove("open");
    sheet.classList.remove("open");
  };
  const open = () => {
    backdrop.classList.add("open");
    sheet.classList.add("open");
  };
  backdrop.addEventListener("click", close);
  closeBtn.addEventListener("click", close);
  return {
    open,
    close,
    body,
    setTitle: (t: string) => (headTitle.textContent = t),
  };
}
